import { useState, useEffect } from 'preact/hooks'
import { AddressSelect, ProjectWithTaskSelect } from '@/components/forms/FuzzySelect'
import CollapsibleFieldset from '@/components/CollapsibleFieldset'
import Input from '@/components/forms/Input'
import { API_DATETIME_driller_prod_FMT } from '@/constants'
import { DateTime } from 'luxon'
import { useContext } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import DrillEquipmentSelect from '@/pages/reports/driller_prod/DrillEquipmentSelect'
import { makeDrillEquipment } from '@/pages/reports/driller_prod/utils'
import Radio from './Radio'
import { getProjectsWithTask } from '@/api/resources'
import { useQuery } from '@tanstack/react-query'
import { GeneralInformation, TaskEntry } from '@/pages/reports/driller_prod/types'
import DrillerProdContext from '@/pages/reports/driller_prod/DrillerProdContext'
import { Fragment } from 'preact'
import { parseDate } from '@/utils'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { fr, enUS } from 'date-fns/locale'

type Props = {
	report?: Api.Report
	type?: ReportType
	disabled?: boolean
}

const roundToNearest15 = (dt: DateTime) => {
	const minutes = dt.minute;
	const rounded = Math.round(minutes / 15) * 15;
	return dt.set({ minute: rounded === 60 ? 0 : rounded, second: 0, millisecond: 0 }).plus({ hour: rounded === 60 ? 1 : 0 });
}

const DrillHeader: FC<Props> = ({ report: r, ...p }) => {
	const { t, i18n } = useTranslation('forms')
	const [date, setDate] = useState(
		r?.date_in ? parseDate(r?.date_in) : roundToNearest15(DateTime.now())
	)
	const context = useContext(DrillerProdContext);
	const generalInformation: GeneralInformation | undefined = context?.generalInformation
	const setGeneralInformation = context?.setGeneralInformation

	const { status: projectsStatus, fetchStatus: projectsFetchStatus, data: projects } = useQuery({
		queryKey: ['projects-tasks'],
		queryFn: getProjectsWithTask,
	})

	const handleProjectMatch = (project: object | undefined) => {
		const projectMatch = project as Api.Project
		const timeEntries = projectMatch?.tasks ? projectMatch.tasks.filter(task => !task.is_archived).map(task => {
			return {
				id: task.id,
				task_id: task.id,
				myoctans_task_id: task.myoctans_task_id,
				BT: task.BT,
				traveling_time: 0,
				work_order_time: 0,
				equipment_categories: task.equipment_categories,
			} as TaskEntry
		}) : []

		setGeneralInformation({
			...generalInformation,
			project_id: projectMatch?.id,
			form: {
				...generalInformation?.form,
				time_entries: timeEntries,
			}
		} as GeneralInformation)
	}

	const workQuarters: Record<any, string> = {
		day: t('driller:day'),
		night: t('driller:night'),
	}

	useEffect(() => {
		if (i18n.language === 'fr') {
			registerLocale('fr', fr)
		}
	}, [i18n.language])

	const now = DateTime.now()
	const defaultCollapsed = r != undefined && !p.disabled

	const workExecutionAddressId = projects?.find((project) => project.id === generalInformation?.project_id)?.supplier_work_execution_address_id

	return (
		<Fragment>
			<CollapsibleFieldset title={t('general_information')} defaultCollapsed={defaultCollapsed}>
				<input type="hidden" name="date_in" value={date.toFormat(API_DATETIME_driller_prod_FMT)} />
				<label>{t('driller:date_in_long')}<span class='text-red-600'>&nbsp;*</span>
					<DatePicker
						wrapperClassName='w-full'
						selected={date.toJSDate()}
						onChange={date => {
							if (!date) return;
							setDate(roundToNearest15(DateTime.fromJSDate(date)));
						}}
						showTimeSelect
						timeFormat="HH:mm"
						timeIntervals={15}
						dateFormat="yyyy-MM-dd' 'HH:mm"
						maxDate={now.toJSDate()}
						locale={i18n.language === 'fr' ? fr : enUS}
						required
					/>
				</label>
				<ProjectWithTaskSelect
					required
					name='project_id'
					selectedId={generalInformation?.project_id}
					label={t('project')}
					onMatch={handleProjectMatch}
				/>
				<AddressSelect
					readonly
					selectedId={workExecutionAddressId}
					name='form[work_execution_address]'
					label={t('work_execution_address')}
					prefered='name'
				/>
				<Input
					name='form[drilling_block]'
					value={generalInformation?.form?.drilling_block}
					label={t('driller:drilling_block')}
					onChange={(e) => setGeneralInformation({ ...generalInformation, form: { ...generalInformation?.form, drilling_block: (e.target as HTMLInputElement).value } })}
				/>
				<Input
					name='form[ditch_number]'
					value={generalInformation?.form?.ditch_number}
					onChange={(e) => setGeneralInformation({ ...generalInformation, form: { ...generalInformation?.form, ditch_number: (e.target as HTMLInputElement).value } })}
					label={t('driller:ditch_number')}
				/>
				<fieldset>
					<legend>{t('driller:quarter')}</legend>
					<Radio
						name='form[quarter]'
						checked={generalInformation?.form.quarter}
						choices={workQuarters}
						onChange={(e) => setGeneralInformation({ ...generalInformation, form: { ...generalInformation?.form, quarter: e } })}
					/>

				</fieldset>
				<DrillEquipmentSelect defaultValue={makeDrillEquipment(r)} />

				<label>{t('driller:drilling_type')}
					<select name={`form[drill_type]`} onChange={(e) => setGeneralInformation({ ...generalInformation, form: { ...generalInformation?.form, drill_type: (e.target as HTMLInputElement).value } })}>
						{/* <option value=''>{t('forms:select_an_option')}</option> */}
						<option selected={generalInformation?.form.drill_type == 'mine'} value='mine'>{t('driller:mine')}</option>
						{/* <option value='quarry'>{t('quarry')}</option> */}
					</select>
				</label>
				<label>{t('driller:units')}
					<select name={`form[units]`} onChange={(e: Event) => setGeneralInformation({ ...generalInformation, form: { ...generalInformation?.form, units: (e.target as HTMLInputElement).value } })}>
						<option selected={generalInformation?.form.units == 'meters'} value='meters'>{t('meters')}</option>
						<option selected={generalInformation?.form.units == 'feet'} value='feet'>{t('feet')}</option>
					</select>
				</label>
			</CollapsibleFieldset>
			<hr />
		</Fragment >
	)
}

export default DrillHeader
